import { useState } from 'react';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Loader2, X, ShieldCheck, Percent, UploadCloud } from 'lucide-react';

export default function InstructorApprovalModal({ instructor, onClose }) {
  const [commissionRate, setCommissionRate] = useState(70);
  const [canUploadCourses, setCanUploadCourses] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', instructor.id), {
        'instructorData.status': 'approved',
        'instructorData.commissionRate': Number(commissionRate),
        'instructorData.canUploadCourses': canUploadCourses
      });
      onClose(); // Parent should handle refresh/closing
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء الموافقة على المدرب');
    } finally {
      setLoading(false);
    }
  };

  if (!instructor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-md animate-in fade-in duration-200" dir="rtl">
      <div className="bg-white dark:bg-zinc-900 rounded-3xl w-full max-w-lg overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.6)] animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="p-8 text-center border-b border-slate-100 dark:border-white/5 relative">
          <button onClick={onClose} className="absolute left-6 top-6 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors bg-slate-100 dark:bg-white/10 p-2 rounded-full">
            <X className="w-5 h-5" />
          </button>
          <div className="w-20 h-20 bg-amber-50 dark:bg-amber-500/10 text-amber-600 mx-auto rounded-full flex items-center justify-center mb-5 border border-amber-100 dark:border-amber-500/20 shadow-inner">
            <ShieldCheck className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white">الموافقة على المُدرب</h2>
          <p className="text-slate-500 dark:text-zinc-400 mt-2 font-medium leading-relaxed">
            تفعيل حساب المدرب <span className="font-bold text-slate-900 dark:text-white">{instructor.name}</span> ومنحه الصلاحيات اللازمة.
          </p>

          {/* Instructor Details Box */}
          <div className="bg-slate-50 dark:bg-black/20 rounded-2xl p-4 text-right mt-5 border border-slate-100 dark:border-white/5 space-y-3 shadow-inner text-md">
            <div className="flex justify-start gap-2">
              <span className="text-slate-500 font-bold w-24 shrink-0">الاسم:</span>
              <span className="text-slate-900 dark:text-white font-black">{instructor.name}</span>
            </div>
            <div className="flex justify-start gap-2">
              <span className="text-slate-500 font-bold w-24 shrink-0">رقم الجوال:</span>
              <span className="text-amber-600 dark:text-amber-400 font-mono font-bold" dir="ltr">{instructor.phone || 'غير متوفر'}</span>
            </div>
            <div className="flex justify-start gap-2">
              <span className="text-slate-500 font-bold w-24 shrink-0">التخصص:</span>
              <span className="text-slate-900 dark:text-white font-bold">{instructor.instructorData?.specialization}</span>
            </div>
          </div>
        </div>

        {/* Form Body */}
        <div className="p-8 space-y-8">

          {/* Commission Rate */}
          <div>
            <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-3 flex items-center gap-2">
              <Percent className="w-5 h-5 text-amber-500" />
              نسبة ربح المدرب من المبيعات
            </label>
            <div className="relative group">
              <input
                type="number"
                min="0"
                max="100"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-black/50 border-2 border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-amber-500 text-2xl font-black text-slate-900 dark:text-white transition-all text-center"
              />
              <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <span className="text-slate-400 font-bold text-xl">%</span>
              </div>
            </div>
            <p className="text-sm text-slate-500 dark:text-zinc-500 mt-2">حدد النسبة المئوية التي سيحصل عليها المدرب من كل عملية شراء لدوراته.</p>
          </div>

          {/* Upload Permissions */}
          <div className="flex items-center justify-between p-5 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/10">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-lg flex items-center gap-2">
                <UploadCloud className="w-5 h-5 text-indigo-500" />
                صلاحية الرفع المباشر
              </h3>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">السماح للمدرب برفع محتوى الدورات بنفسه</p>
            </div>
            <button
              type="button"
              onClick={() => setCanUploadCourses(!canUploadCourses)}
              className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${canUploadCourses ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-zinc-700'}`}
            >
              <span
                className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${canUploadCourses ? '-translate-x-6' : 'translate-x-0'}`}
              />
            </button>
          </div>

          <div className="pt-4 flex flex-col gap-3">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white py-4 rounded-2xl font-bold text-lg transition-all shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 flex justify-center items-center gap-2"
            >
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  اعتماد وتفعيل حساب المدرب
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
